// Common Zod refinements reused across the engine, recipes, and api-types.
//
// Project-wide rule: every cross-boundary field that has shape rules (RUT,
// email, slug, ID) MUST validate through one of these helpers, never with an
// ad-hoc string regex elsewhere. If a vertical needs a different rule, the
// recipe overrides at its own boundary — the engine baseline stays canonical.
import { z } from "zod";
import { computeRutCheckDigit } from "./rut.js";
// UUIDv4 — engine entities are client-generatable so the offline-first
// frontend (per v3.md) can mint IDs without a network round trip.
// `crypto.randomUUID()` produces RFC 4122 v4 strings.
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
export const uuidSchema = z.string().regex(UUID_REGEX, { message: "must be a UUIDv4" });
// Recipe slug, org-config keys, finding categories — short, lowercase,
// underscored. Matches v2's `recipe_slug` shape (`residential_rental`,
// `broker_postventa`).
export const slugSchema = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z][a-z0-9_]*$/, {
    message: "must be lowercase letters, digits, and underscores; must start with a letter",
});
export const emailSchema = z.string().email().max(254).toLowerCase();
// Chilean RUT (Rol Único Tributario). Accepted as either "12.345.678-5" or
// "12345678-5"; we normalize to the no-dot form. The check digit is verified
// using the standard mod-11 algorithm.
//
// This is engine-baseline. A vertical that doesn't use Chilean tax IDs (e.g.
// future regional verticals) should override `parties[].id_doc` validation in
// its recipe and skip this helper.
//
// El cálculo del verificador vive en ./rut.ts (funciones puras, sin zod)
// para que una ruta o un cliente lo use fuera de un schema sin duplicarlo.
const RUT_RAW = /^(\d{1,3}(?:\.\d{3}){0,2}|\d{1,8})-([0-9kK])$/;
export const rutSchema = z
    .string()
    .trim()
    .transform((value, ctx) => {
    const match = RUT_RAW.exec(value);
    if (!match) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "invalid RUT format" });
        return z.NEVER;
    }
    const body = match[1].replaceAll(".", "");
    const provided = match[2].toUpperCase();
    const expected = computeRutCheckDigit(body);
    if (provided !== expected) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "invalid RUT check digit" });
        return z.NEVER;
    }
    return `${body}-${expected}`;
});
// ISO 8601 date string. Accepts both date-only (`2026-04-29`) and full
// timestamps. Used for delivery dates, signed_at, etc.
export const isoDateTimeSchema = z.string().refine((value) => !Number.isNaN(Date.parse(value)), {
    message: "must be ISO 8601",
});
// Phone number — permissive on purpose. We carry the raw string the user
// typed and let the recipe / vertical decide whether to enforce a country
// format. Empty string is rejected; whitespace is trimmed.
export const phoneSchema = z.string().trim().min(4).max(32);
//# sourceMappingURL=zod.js.map